import React, { useState, useEffect, useRef, useCallback } from "react";

// PageMenu renders a sticky "Sections" sidebar next to its content. The list of
// sections is derived from the rendered DOM (every `.stats-box` and its own
// `.stats-title`), NOT from walking the React children. That matters because on
// the record pages the StatsBoxes are returned from inside EnzymeRecord /
// MoleculeRecord, so they are not reachable as `props.children` here — a
// children walk would only see the <EnzymeRecord/> element and stop at that
// component boundary. Scanning the DOM works regardless of how deeply the boxes
// are nested behind other components, and a MutationObserver keeps the menu in
// sync as async sections (mechanism viewer, structure block, …) mount later.
function PageMenu({ children }) {
  const contentRef = useRef(null);
  const rebuildTimer = useRef(null);
  // Monotonic counter for the ids we stamp on the boxes. It must NOT be the
  // box's index in the current scan: a section that mounts late (mechanism
  // viewer, structure block, the motifs box inside the sequence section) shifts
  // every following index, so an index-derived id collides with the id an
  // already-stamped box was given in an earlier scan. Duplicate ids become
  // duplicate React keys below, and the colliding entry then renders twice.
  const nextSectionId = useRef(0);
  const [items, setItems] = useState([]);

  const rebuild = useCallback(() => {
    const root = contentRef.current;
    if (!root) return;

    const boxes = Array.from(root.querySelectorAll(".stats-box"));
    const seenIds = new Set();
    const next = boxes.map(box => {
      // Restamp any id we've already handed out in this scan too — a duplicate
      // id is invalid HTML anyway, and it would break both the menu keys and
      // the scroll target lookup.
      if (!box.id || seenIds.has(box.id)) box.id = `pm-section-${nextSectionId.current++}`;
      seenIds.add(box.id);
      const titleEl = box.querySelector(":scope > .stats-title");
      const title = (titleEl?.textContent || "").trim() || "Untitled";
      // Preserve any inline markup in the title (e.g. a leading FontAwesome
      // icon) so the menu mirrors the section heading. textContent above is
      // still used for the dedup comparison and as the fallback label.
      const titleHtml = titleEl?.innerHTML || "";

      // Indentation level = number of ancestor .stats-box up to the content root.
      let level = 0;
      let parent = box.parentElement;
      while (parent && parent !== root) {
        if (parent.classList?.contains("stats-box")) level++;
        parent = parent.parentElement;
      }
      return { id: box.id, title, titleHtml, level, node: box };
    });

    // Avoid re-rendering when nothing relevant changed (the observer fires for
    // every DOM mutation inside the content, e.g. chart/graph animations).
    setItems(prev => {
      if (
        prev.length === next.length &&
        prev.every((p, i) =>
          p.id === next[i].id && p.title === next[i].title &&
          p.titleHtml === next[i].titleHtml && p.level === next[i].level
        )
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const root = contentRef.current;
    if (!root) return;

    const scheduleRebuild = () => {
      if (rebuildTimer.current) return;
      rebuildTimer.current = setTimeout(() => {
        rebuildTimer.current = null;
        rebuild();
      }, 100);
    };

    rebuild();
    // Watch only childList/subtree (node add/remove). We mutate box.id above,
    // but attribute changes aren't observed, so this can't loop.
    const observer = new MutationObserver(scheduleRebuild);
    observer.observe(root, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      if (rebuildTimer.current) {
        clearTimeout(rebuildTimer.current);
        rebuildTimer.current = null;
      }
    };
  }, [rebuild, children]);

  const handleClick = id => {
    const node = items.find(i => i.id === id)?.node;
    if (!node) return;
    // The navbar is position:fixed, so scrolling the section to the very top
    // would tuck its title under the navbar. Offset by the (measured) navbar
    // height plus a small gap.
    const navbar = document.getElementById("HomeNavbar");
    const offset = (navbar?.offsetHeight || 60) + 12;
    const top = node.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
  };

  return (
    <div style={{ display: "flex", alignItems: "flex-start" }}>
      {/* Left menu — hidden on small screens via the .page-menu media query */}
      {items.length > 0 && (
        <div className="page-menu" id="pagemenu">
          <h2 style={{ fontSize: "17px", fontWeight: "bold", marginBottom: "12px" }}>Sections</h2>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {items.map(item => (
              <li
                key={item.id}
                style={{
                  marginBottom: "6px",
                  paddingLeft: item.level * 12, // indent by level
                }}
              >
                <button
                  onClick={() => handleClick(item.id)}
                  id="menubutton"
                  {...(item.titleHtml
                    ? { dangerouslySetInnerHTML: { __html: item.titleHtml } }
                    : { children: item.title })}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Main content */}
      <div id="basicpage" ref={contentRef}>
        {children}
      </div>
    </div>
  );
}

export default PageMenu;
